import React, { useEffect, useRef, useState } from "react";
import { Pencil, Plus, RefreshCw, Search, Trash2, X } from "lucide-react";
import * as FaIcons from "react-icons/fa";
import { toast } from "react-toastify";
import { useMenuHubApi } from "../../api/menuHub";
import AccessTablePagination from "../../components/access-table-pagination";
import SolutionFormModal from "../../components/solution-form-modal";
import {
    MobileSolutionsSkeleton,
    Skeleton,
    SolutionsTableSkeleton,
} from "../../components/skeletons";
import { useTheme } from "../../theme/useTheme";
import { AdminPagination, AdminSolution } from "../../types";
import "./styles.scss";

interface SolutionsPanelProps {
    active?: boolean;
}

const PAGE_SIZE = 8;

const ESTABLISHMENT_LABELS: Record<string, string> = {
    "2": "Hospital São Marcelino Champagnat",
    "33": "Hospital Universitário Cajuru",
};

const getEstablishmentLabel = (code?: string) => (
    code ? ESTABLISHMENT_LABELS[code] || "" : ""
);

const SolutionIcon: React.FC<{ name?: string; isDarkMode?: boolean }> = ({
    name,
    isDarkMode = false,
}) => {
    const [iconInfo, setIconInfo] = useState<{
        Component?: React.FC<React.SVGProps<SVGSVGElement>>;
        src?: string;
    }>();
    const [loading, setLoading] = useState(Boolean(name));

    useEffect(() => {
        let cancelled = false;

        if (!name) {
            setIconInfo(undefined);
            setLoading(false);
            return;
        }

        setLoading(true);

        const loadIcon = async () => {
            try {
                const iconModule = await import(
                    `../../assets/icons/fontawesome/svgs/regular/${name}`
                );

                if (!cancelled) {
                    setIconInfo(
                        iconModule.ReactComponent
                            ? { Component: iconModule.ReactComponent }
                            : { src: iconModule.default },
                    );
                }
            } catch {
                if (!cancelled) {
                    setIconInfo(undefined);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadIcon();

        return () => {
            cancelled = true;
        };
    }, [name]);

    if (loading) {
        return <Skeleton height={18} width={18} borderRadius={4} />;
    }

    const LoadedIcon = iconInfo?.Component;
    const IconFallback = name ? (FaIcons as any)[name] : undefined;
    const Fallback = IconFallback || FaIcons.FaRegFileAlt;
    const color = isDarkMode ? "#b5b9c8" : "#5c5656";
    const filter = isDarkMode
        ? "brightness(0) saturate(100%) invert(75%)"
        : "brightness(0) saturate(100%) invert(36%)";

    return (
        <span className="solution-icon" aria-label={name || "Ícone não informado"} title={name}>
            {LoadedIcon ? (
                <LoadedIcon width={18} height={18} fill={color} />
            ) : iconInfo?.src ? (
                <img
                    src={iconInfo.src}
                    alt={name || "Ícone da solução"}
                    width={18}
                    height={18}
                    style={{ filter }}
                />
            ) : (
                <Fallback size={18} color={color} />
            )}
        </span>
    );
};

const SolutionsPanel: React.FC<SolutionsPanelProps> = ({ active = true }) => {
    const api = useMenuHubApi();
    const apiRef = useRef(api);
    apiRef.current = api;

    const { isDarkMode } = useTheme();
    const [search, setSearch] = useState("");
    const [solutions, setSolutions] = useState<AdminSolution[]>([]);
    const [pagination, setPagination] = useState<AdminPagination | null>(null);
    const [page, setPage] = useState(0);
    const [loadingSolutions, setLoadingSolutions] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);
    const [formMode, setFormMode] = useState<"create" | "edit">("create");
    const [selectedSolution, setSelectedSolution] = useState<AdminSolution | null>(null);
    const [formOpen, setFormOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<AdminSolution | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [requestError, setRequestError] = useState("");

    useEffect(() => {
        if (!active) {
            return;
        }

        let cancelled = false;
        setLoadingSolutions(true);
        setRequestError("");

        const loadSolutions = async () => {
            try {
                const response = await apiRef.current.getSolucoes(search, PAGE_SIZE, page);
                if (!cancelled) {
                    setSolutions(response?.solucoes ?? []);
                    setPagination(response?.pagination ?? null);
                }
            } catch {
                if (!cancelled) {
                    setSolutions([]);
                    setPagination(null);
                    setRequestError("Não foi possível carregar as soluções.");
                }
            } finally {
                if (!cancelled) {
                    setLoadingSolutions(false);
                }
            }
        };

        loadSolutions();

        return () => {
            cancelled = true;
        };
    }, [active, page, reloadKey, search]);

    useEffect(() => {
        if (!deleteTarget || deleting) {
            return;
        }

        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setDeleteTarget(null);
            }
        };

        document.addEventListener("keydown", closeOnEscape);
        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [deleteTarget, deleting]);

    const openCreateForm = () => {
        setFormMode("create");
        setSelectedSolution(null);
        setFormOpen(true);
    };

    const openEditForm = (solution: AdminSolution) => {
        setFormMode("edit");
        setSelectedSolution(solution);
        setFormOpen(true);
    };

    const resetSearch = () => {
        setSearch("");
        setPage(0);
    };

    const handleSaved = () => {
        setPage(0);
        setReloadKey((current) => current + 1);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) {
            return;
        }

        setDeleting(true);
        setRequestError("");

        try {
            await apiRef.current.deleteSolucao(deleteTarget.id);
            toast.dismiss();
            toast.success("Solução excluída com sucesso");
            setDeleteTarget(null);

            if (solutions.length === 1 && page > 0) {
                setPage((current) => current - 1);
            } else {
                setReloadKey((current) => current + 1);
            }
        } catch {
            setRequestError("Não foi possível excluir a solução.");
        } finally {
            setDeleting(false);
        }
    };

    const renderActions = (solution: AdminSolution, mobile = false) => (
        <div className={`solution-row-actions${mobile ? " solution-row-actions--mobile" : ""}`}>
            <button
                type="button"
                className="solution-action"
                aria-label={`Editar ${solution.nome}`}
                title="Editar"
                onClick={() => openEditForm(solution)}
            >
                <Pencil size={16} />
            </button>
            <button
                type="button"
                className="solution-action solution-action--delete"
                aria-label={`Excluir ${solution.nome}`}
                title="Excluir"
                onClick={() => setDeleteTarget(solution)}
            >
                <Trash2 size={16} />
            </button>
        </div>
    );

    const totalPages = Math.max(1, Number(pagination?.totalPages) || 1);
    const currentPage = Math.min(page + 1, totalPages);
    const totalCount = Math.max(0, Number(pagination?.totalCount) || 0);
    const itemsPerPage = Math.max(1, Number(pagination?.totalItemsPerPage) || PAGE_SIZE);
    const itemsInPage = Math.max(0, Number(pagination?.itemsInThePage) || solutions.length);
    const rangeStart = itemsInPage > 0 ? page * itemsPerPage + 1 : 0;
    const rangeEnd = itemsInPage > 0
        ? Math.min(rangeStart + itemsInPage - 1, totalCount)
        : 0;
    const showInitialSkeleton = loadingSolutions && solutions.length === 0 && !pagination;

    return (
        <>
            <section className="solutions-panel">
                <header className="solutions-page-header">
                    <h1>Soluções</h1>
                    <button
                        type="button"
                        className="new-solution-button"
                        onClick={openCreateForm}
                    >
                        <Plus size={16} />
                        <span className="new-solution-button__desktop-label">Nova solução</span>
                        <span className="new-solution-button__mobile-label">Nova</span>
                    </button>
                </header>

                <div className="solutions-card">
                    <div className="solutions-toolbar">
                        <div className="solutions-search">
                            <Search size={16} className="solutions-search__icon" />
                            <input
                                type="text"
                                placeholder="Buscar solução..."
                                aria-label="Buscar pelo nome da solução"
                                value={search}
                                onChange={(event) => {
                                    setSearch(event.target.value);
                                    setPage(0);
                                }}
                            />
                            {search && (
                                <button
                                    type="button"
                                    aria-label="Limpar busca"
                                    onClick={() => {
                                        setSearch("");
                                        setPage(0);
                                    }}
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                        <button type="button" className="solutions-reset" onClick={resetSearch}>
                            <RefreshCw size={16} />
                            <span>Redefinir</span>
                        </button>
                    </div>

                    {requestError && (
                        <p className="solutions-request-error" role="alert">{requestError}</p>
                    )}

                    <div className="solutions-desktop-table">
                        {showInitialSkeleton ? (
                            <SolutionsTableSkeleton isDarkMode={isDarkMode} />
                        ) : (
                            <table>
                                <thead>
                                    <tr>
                                        <th>Estabelecimento</th>
                                        <th>Título</th>
                                        <th>Solução</th>
                                        <th className="solution-icon-column">Ícone</th>
                                        <th>HREF</th>
                                        <th className="solution-actions-column">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {solutions.length ? (
                                        solutions.map((solution) => (
                                            <tr key={solution.id}>
                                                <td title={getEstablishmentLabel(solution.cdEstabelecimento)}>
                                                    {getEstablishmentLabel(solution.cdEstabelecimento)}
                                                </td>
                                                <td title={solution.titulo}>{solution.titulo || "—"}</td>
                                                <td title={solution.nome}>{solution.nome}</td>
                                                <td>
                                                    <div className="solution-icon-cell">
                                                        <SolutionIcon
                                                            name={solution.icone}
                                                            isDarkMode={isDarkMode}
                                                        />
                                                    </div>
                                                </td>
                                                <td title={solution.href}>{solution.href || "—"}</td>
                                                <td>{renderActions(solution)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={6} className="solutions-empty-state">
                                                Nenhum registro encontrado.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="solutions-mobile-list">
                        {showInitialSkeleton ? (
                            <MobileSolutionsSkeleton isDarkMode={isDarkMode} />
                        ) : solutions.length ? (
                            solutions.map((solution) => (
                                <article key={solution.id} className="solution-mobile-card">
                                    <div className="solution-mobile-card__header">
                                        <div className="solution-mobile-card__identity">
                                            <h2>{solution.nome}</h2>
                                            <p>{solution.titulo || "Sem título"}</p>
                                            {getEstablishmentLabel(solution.cdEstabelecimento) && (
                                                <span>
                                                    Estabelecimento: {getEstablishmentLabel(
                                                        solution.cdEstabelecimento,
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                        {renderActions(solution, true)}
                                    </div>
                                    <div className="solution-mobile-card__link">
                                        <SolutionIcon
                                            name={solution.icone}
                                            isDarkMode={isDarkMode}
                                        />
                                        <span>{solution.href || "—"}</span>
                                    </div>
                                </article>
                            ))
                        ) : (
                            <p className="solutions-empty-state">Nenhum registro encontrado.</p>
                        )}
                    </div>
                </div>

                {!showInitialSkeleton && totalCount > 0 && (
                    <footer className="solutions-pagination-footer">
                        <p>Mostrando {rangeStart} a {rangeEnd} de {totalCount} registros</p>
                        <AccessTablePagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            disabled={loadingSolutions}
                            ariaLabel="Paginação de soluções"
                            hasPreviousPage={currentPage > 1}
                            hasNextPage={currentPage < totalPages}
                            onPageChange={(nextPage) => setPage(nextPage - 1)}
                        />
                    </footer>
                )}
            </section>

            <SolutionFormModal
                open={formOpen}
                mode={formMode}
                solution={selectedSolution}
                onClose={() => setFormOpen(false)}
                onSaved={handleSaved}
            />

            {deleteTarget && (
                <div
                    className="solutions-modal-overlay solutions-modal-overlay--delete"
                    role="presentation"
                    onMouseDown={deleting ? undefined : () => setDeleteTarget(null)}
                >
                    <section
                        className="solution-delete-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="solution-delete-title"
                        aria-describedby="solution-delete-description"
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <header className="solutions-modal-header">
                            <h2 id="solution-delete-title">Excluir solução</h2>
                            <button
                                type="button"
                                className="solutions-modal-close"
                                aria-label="Fechar"
                                onClick={() => setDeleteTarget(null)}
                                disabled={deleting}
                            >
                                <X size={18} />
                            </button>
                        </header>
                        <p id="solution-delete-description">
                            Você tem certeza que deseja excluir a solução{" "}
                            <strong>{deleteTarget.nome}</strong>? Esta ação não pode ser desfeita
                        </p>
                        <div className="solution-form-actions">
                            <button
                                type="button"
                                className="solution-button solution-button--secondary"
                                onClick={() => setDeleteTarget(null)}
                                disabled={deleting}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="solution-button solution-button--primary"
                                onClick={confirmDelete}
                                disabled={deleting}
                                autoFocus
                            >
                                {deleting ? "Excluindo..." : "Excluir"}
                            </button>
                        </div>
                    </section>
                </div>
            )}
        </>
    );
};

export default SolutionsPanel;
